/**
 * ==========================================
 * خدمة إدارة الكوبونات المركزية
 * ==========================================
 * 
 * هذه الخدمة مسؤولة عن جميع عمليات الكوبونات:
 * - التحقق من الكوبونات
 * - تطبيق الكوبونات التلقائية
 * - حساب الخصومات
 * - إدارة حالة الكوبونات المطبقة
 * 
 * الهدف: مركزية المنطق وتسهيل الصيانة
 */

import { API_BASE_URL } from '../config';

// ==================== الأنواع ====================

/** بيانات الكوبون المتاح */
export interface AvailableCoupon {
  code: string;
  description: string;
  promotionName: string;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
}

/** عنصر الفاتورة */
export interface InvoiceItem {
  id?: string;
  productId?: string;
  variantId?: string;
  quantity?: number;
  price?: number;
  originalPrice?: number;
  [key: string]: any;
}

/** إعدادات اشتر X واحصل على Y */
export interface BuyXGetYConfig {
  primaryProducts: string[];
  secondaryProducts: string[];
  primaryQuantity: number;
  secondaryQuantity: number;
}

/** نتيجة حساب الخصم */
export interface DiscountCalculation {
  discountAmount: number;
  message?: string;
  comboApplied?: boolean;
  targetPriceApplied?: boolean;
  schemeType?: string;
  eligibleItems?: EligibleItem[];
}

/** عنصر مؤهل للخصم */
export interface EligibleItem {
  productId?: string;
  variantId?: string;
  quantity?: number;
  discountPerUnit?: number;
  [key: string]: any;
}

/** نتيجة التحقق من الكوبون */
export interface CouponVerificationResult {
  valid: boolean;
  discount?: number;
  discountType?: 'percentage' | 'fixed';
  message?: string;
  eligibleProducts?: string[];
  eligibleVariants?: string[];
  eligibleCategories?: string[];
  eligibleItems?: EligibleItem[];
  buyXGetYConfig?: BuyXGetYConfig;
  discountCalculation?: DiscountCalculation;
}

/** استجابة API التحقق من الكوبون */
interface CouponVerifyResponse {
  success: boolean;
  message?: string;
  coupon?: {
    discountValue: number;
    discountType: 'percentage' | 'fixed';
    eligibleProducts?: string[];
    eligibleCategories?: string[];
    schemeType?: string;
    buyXGetYConfig?: BuyXGetYConfig;
  };
  discountCalculation?: DiscountCalculation;
  eligibleProductDetails?: Array<{
    productId?: string;
    variantId?: string;
    productName?: string;
    variants?: Array<{ variantId?: string }>;
  }>;
}

/** الكوبون المطبق */
export interface AppliedCoupon {
  code: string;
  discount: number;
  discountType: 'percentage' | 'fixed';
  eligibleProducts?: string[];
  eligibleVariants?: string[];
  eligibleCategories?: string[];
  eligibleItems?: EligibleItem[];
  buyXGetYConfig?: BuyXGetYConfig;
  discountCalculation?: DiscountCalculation;
  appliedAt: Date;
}

/** حالة الكوبونات */
export interface CouponState {
  appliedCoupons: AppliedCoupon[];
  availableCoupons: AvailableCoupon[];
  totalDiscount: number;
}

// ==================== الدوال المساعدة ====================

/**
 * استخراج وتطبيع معرفات المتغيرات من المنتج
 */
const extractVariantIds = (item: any): string[] => {
  const ids: string[] = [];
  
  const normalizeId = (id: any): string[] => {
    if (!id) return [];
    const cleanId = String(id).trim();
    if (!cleanId) return [];
    
    const result = [cleanId];
    
    // معالجة المعرفات التي تحتوي على /
    if (cleanId.includes('/')) {
      const segments = cleanId.split('/').map(s => s.trim()).filter(s => s.length > 5);
      result.push(...segments);
    }
    
    // إزالة التكرارات
    return result.filter((value, index, self) => self.indexOf(value) === index);
  };
  
  // استخراج من variantId
  if (item.variantId) {
    ids.push(...normalizeId(item.variantId));
  }
  
  // استخراج من variants array
  if (Array.isArray(item.variants)) {
    item.variants.forEach((v: any) => {
      if (v.variantId) {
        ids.push(...normalizeId(v.variantId));
      }
    });
  }
  
  // استخراج من productId كبديل
  if (item.productId) {
    ids.push(...normalizeId(item.productId));
  }
  
  // إزالة التكرارات
  return ids.filter((value, index, self) => self.indexOf(value) === index);
};

// ==================== الخدمة المركزية ====================

class CouponManager {
  private abortController: AbortController | null = null;

  /**
   * التحقق من صلاحية الكوبون
   */
  async verifyCoupon(
    code: string,
    invoiceItems: InvoiceItem[],
    pageId?: string,
    appliedCoupons: string[] = []
  ): Promise<CouponVerificationResult> {
    // إلغاء الطلب السابق إن وجد
    if (this.abortController) {
      this.abortController.abort();
    }
    
    // إنشاء controller جديد
    this.abortController = new AbortController();

    try {
      const response = await fetch(`${API_BASE_URL}/coupons/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        signal: this.abortController.signal,
        body: JSON.stringify({
          code: code.trim(),
          invoiceId: null,
          pageId,
          appliedCoupons,
          invoice: {
            items: invoiceItems.map(item => ({
              ...item,
              price: item.originalPrice || item.price,
              originalPrice: item.originalPrice || item.price
            }))
          }
        })
      });

      const data: CouponVerifyResponse = await response.json();

      if (!response.ok || !data.success) {
        return {
          valid: false,
          message: data.message || 'كود الكوبون غير صالح أو منتهي الصلاحية'
        };
      }

      // معالجة الاستجابة الناجحة
      const coupon = data.coupon!;
      const discountCalc = data.discountCalculation;
      
      // التحقق من صلاحية الخصم
      const isDiscountValid = !discountCalc || discountCalc.discountAmount > 0;
      
      // استخراج المنتجات والمتغيرات المؤهلة
      let eligibleProducts: string[] = [...(coupon.eligibleProducts || [])];
      let eligibleVariants: string[] = [];
      
      // استخراج من eligibleProductDetails
      if (data.eligibleProductDetails?.length) {
        data.eligibleProductDetails.forEach(item => {
          const variantIds = extractVariantIds(item);
          eligibleVariants.push(...variantIds);
          if (item.productId) eligibleProducts.push(item.productId);
        });
      }
      
      // استخراج من discountCalculation.eligibleItems
      if (discountCalc?.eligibleItems?.length) {
        discountCalc.eligibleItems.forEach(item => {
          if (item.productId) eligibleProducts.push(item.productId);
          if (item.variantId) eligibleVariants.push(item.variantId);
        });
      }
      
      // معالجة خاصة لحملة buy_x_get_y
      if (coupon.schemeType === 'buy_x_get_y' && coupon.buyXGetYConfig) {
        if (isDiscountValid) {
          eligibleProducts = coupon.buyXGetYConfig.secondaryProducts || [];
        } else {
          eligibleProducts = [];
          eligibleVariants = [];
        }
      }
      
      // إزالة التكرارات
      eligibleProducts = eligibleProducts.filter((value, index, self) => self.indexOf(value) === index);
      eligibleVariants = eligibleVariants.filter((value, index, self) => self.indexOf(value) === index);

      return {
        valid: isDiscountValid,
        discount: discountCalc?.discountAmount || coupon.discountValue,
        discountType: discountCalc?.discountAmount ? 'fixed' : coupon.discountType,
        message: isDiscountValid
          ? 'تم التحقق من الكوبون بنجاح - تم تطبيق الخصم على المنتجات المشاركة'
          : (discountCalc?.message || 'لا يمكن تطبيق الخصم على الفاتورة الحالية'),
        eligibleProducts,
        eligibleVariants,
        eligibleCategories: coupon.eligibleCategories || [],
        eligibleItems: discountCalc?.eligibleItems || [],
        buyXGetYConfig: coupon.buyXGetYConfig,
        discountCalculation: discountCalc
      };
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        throw error;
      }
      console.error('خطأ في التحقق من الكوبون:', error);
      return {
        valid: false,
        message: 'حدث خطأ أثناء التحقق من الكوبون'
      };
    }
  }

  /**
   * تطبيق كوبون واحد
   */
  async applyCoupon(
    code: string,
    invoiceItems: InvoiceItem[],
    currentAppliedCoupons: AppliedCoupon[],
    pageId?: string
  ): Promise<{ success: boolean; coupon?: AppliedCoupon; message?: string }> {
    if (!code?.trim() || code.trim().length < 3) {
      return {
        success: false,
        message: 'كود الكوبون غير صالح - يجب أن يكون 3 أحرف على الأقل'
      };
    }

    // التحقق من عدم تطبيق الكوبون مسبقاً
    if (currentAppliedCoupons.some(c => c.code === code)) {
      return {
        success: false,
        message: 'هذا الكوبون مطبق بالفعل'
      };
    }

    try {
      const appliedCouponCodes = currentAppliedCoupons.map(c => c.code);
      const result = await this.verifyCoupon(code, invoiceItems, pageId, appliedCouponCodes);

      if (!result.valid) {
        return {
          success: false,
          message: result.message
        };
      }

      const appliedCoupon: AppliedCoupon = {
        code,
        discount: result.discount || 0,
        discountType: result.discountType || 'fixed',
        eligibleProducts: result.eligibleProducts,
        eligibleVariants: result.eligibleVariants,
        eligibleCategories: result.eligibleCategories,
        eligibleItems: result.eligibleItems,
        buyXGetYConfig: result.buyXGetYConfig,
        discountCalculation: result.discountCalculation,
        appliedAt: new Date()
      };

      return {
        success: true,
        coupon: appliedCoupon,
        message: result.message
      };
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        throw error;
      }
      console.error('خطأ في تطبيق الكوبون:', error);
      return {
        success: false,
        message: 'حدث خطأ أثناء تطبيق الكوبون'
      };
    }
  }

  /**
   * تطبيق عدة كوبونات دفعة واحدة
   */
  async applyMultipleCoupons(
    codes: string[],
    invoiceItems: InvoiceItem[],
    currentAppliedCoupons: AppliedCoupon[],
    pageId?: string
  ): Promise<{
    successfulCoupons: AppliedCoupon[];
    failedCoupons: Array<{ code: string; message: string }>;
  }> {
    const successfulCoupons: AppliedCoupon[] = [];
    const failedCoupons: Array<{ code: string; message: string }> = [];

    for (const code of codes) {
      try {
        const result = await this.applyCoupon(
          code,
          invoiceItems,
          [...currentAppliedCoupons, ...successfulCoupons],
          pageId
        );

        if (result.success && result.coupon) {
          successfulCoupons.push(result.coupon);
        } else {
          failedCoupons.push({ code, message: result.message || 'فشل التطبيق' });
        }

        // انتظار قصير بين الكوبونات
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (error) {
        if ((error as Error).name === 'AbortError') {
          continue;
        }
        failedCoupons.push({ code, message: 'حدث خطأ أثناء التطبيق' });
      }
    }

    return { successfulCoupons, failedCoupons };
  }

  /**
   * إعادة حساب كوبون مطبق (عند تغيير المنتجات)
   */
  async recalculateCoupon(
    coupon: AppliedCoupon,
    invoiceItems: InvoiceItem[],
    otherAppliedCoupons: AppliedCoupon[],
    pageId?: string
  ): Promise<{ success: boolean; coupon?: AppliedCoupon; shouldRemove?: boolean }> {
    try {
      const otherCouponCodes = otherAppliedCoupons.map(c => c.code);
      const result = await this.verifyCoupon(coupon.code, invoiceItems, pageId, otherCouponCodes);

      if (!result.valid) {
        return {
          success: false,
          shouldRemove: true
        };
      }

      const updatedCoupon: AppliedCoupon = {
        ...coupon,
        discount: result.discount || 0,
        discountType: result.discountType || 'fixed',
        eligibleProducts: result.eligibleProducts,
        eligibleVariants: result.eligibleVariants,
        eligibleCategories: result.eligibleCategories,
        eligibleItems: result.eligibleItems,
        buyXGetYConfig: result.buyXGetYConfig,
        discountCalculation: result.discountCalculation
      };

      return {
        success: true,
        coupon: updatedCoupon
      };
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        throw error;
      }
      console.error('خطأ في إعادة حساب الكوبون:', error);
      return {
        success: false,
        shouldRemove: false
      };
    }
  }

  /**
   * إعادة حساب جميع الكوبونات المطبقة
   */
  async recalculateAllCoupons(
    appliedCoupons: AppliedCoupon[],
    invoiceItems: InvoiceItem[],
    pageId?: string
  ): Promise<AppliedCoupon[]> {
    const recalculatedCoupons: AppliedCoupon[] = [];

    for (const coupon of appliedCoupons) {
      try {
        // عدم تمرير الكوبونات الأخرى - كل كوبون يُحسب بشكل مستقل
        // هذا يسمح بتطبيق عدة كوبونات على نفس المنتجات
        const result = await this.recalculateCoupon(
          coupon,
          invoiceItems,
          [], // مصفوفة فارغة بدلاً من recalculatedCoupons
          pageId
        );

        if (result.success && result.coupon) {
          recalculatedCoupons.push(result.coupon);
        } else if (result.shouldRemove) {
          console.log(`إزالة الكوبون ${coupon.code} - لم يعد صالحاً`);
        }

        // انتظار قصير بين الكوبونات
        await new Promise(resolve => setTimeout(resolve, 150));
      } catch (error) {
        if ((error as Error).name === 'AbortError') {
          continue;
        }
        console.error(`خطأ في إعادة حساب الكوبون ${coupon.code}:`, error);
      }
    }

    return recalculatedCoupons;
  }

  /**
   * حساب إجمالي الخصم من جميع الكوبونات المطبقة
   */
  calculateTotalDiscount(appliedCoupons: AppliedCoupon[]): number {
    return appliedCoupons.reduce((total, coupon) => {
      return total + (coupon.discountCalculation?.discountAmount || coupon.discount || 0);
    }, 0);
  }

  /**
   * إلغاء جميع الطلبات الجارية
   */
  cancelPendingRequests(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }
}

// تصدير instance واحد من الخدمة
export const couponManager = new CouponManager();

// تصدير الكلاس للاستخدام في الاختبارات
export default CouponManager;
